import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import draftToHtml from 'draftjs-to-html';
import parse from 'html-react-parser';
import { OnlyMobileAllowed, QRVerificationPage, SendSMS } from '@containers';
import { LoadingSpinner } from '@FLOW_V2_FLOW/components';
import Message from '@lib/components/v2/Message';
import { LockedTransactionUrl } from '@lib/components/v2/errors';
import { generateId, getCookie, setCookie } from '@js/lib/Utils';
import { cleanAppCookies } from '@lib/utils/appCookies';
import { getTransactionToken } from '@lib/utils/transactionToken';
import { getLocalizedIdvcContent, localizedString } from '@languages';
import {
  MOBILE_DEVICE_CHECK_COOKIE,
  NON_MOBILE_DEVICE_SCREEN_TYPES
} from '@lib/constants/nonMobileDeviceScreenTypes';
import isMobile from '@lib/utils/mobile-detection';
import { NON_MOBILE_DEVICE_FALLBACK, UNIVERSAL_URL_PATH } from '@spotMobileConfig';
import { withDetailMatch } from '@js/lib/components/v2/App/DetailMatch';
import { withDeviceMotionDetectionProvider } from '@js/lib/components/v2/App/DeviceMotionDetection';
import { compose } from 'redux';
import AppConfigAction from '../store/actions/appConfig';
import { setIpCountryCodeAction } from '../store/actions/ipInfo';
import { AppContainer as AppFlowV2Container } from '../_FLOW_V2_FLOW/containers';
import { AppContainer as AppVOIFlowV2Container } from '../_VOIFLOW_V2_FLOW/containers';
import { AppContainer as AppQOnlyContainer } from '../_Q_ONLY_FLOW/containers';
import APIs from '../services/APIs';

import '../styles/base/base.scss';

class AppView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      verify: false,
      flowType: '',
      isLoading: true,
      isCompleted: false,
      transStatus: '',
      questionnaire: null,
      hasLivenessScreen: true,
      error: null,
      isIncorrectUrl: false,
      nonMobile: false
    };
  }

  componentDidMount() {
    const { setAppConfig, setIpCountryCode } = this.props;

    const transToken = getTransactionToken();
    const storedTransToken = getCookie('transToken');
    const detailMatch = getCookie('detailMatch');

    if (transToken !== storedTransToken) {
      // For Detail match, when initially load transaction using detail match criteria transToken cookie will be empty
      // Do not clear coockie in such case
      // otherwise detialMatch cookie will be reset causing detail match component rendered again
      if (detailMatch !== 'yes' || storedTransToken) {
        cleanAppCookies();
      }
      setCookie('transToken', transToken, 1 / 24);
    }

    const countryRequestPromise = APIs.country()
      .then((countryInfoResponse) => {
        const { country_code: countryCode } = countryInfoResponse;
        setIpCountryCode(countryCode);
      })
      .catch(() => {
        this.setState({ isIncorrectUrl: true });
      });

    if (document.body.dataset.primary) {
      document.documentElement.style.setProperty('--primary', document.body.dataset.primary);
      document.documentElement.style.setProperty('--loading-bg', document.body.dataset.primary);
      document.documentElement.style.setProperty(
        '--loading-progress-bar-incomplete',
        document.body.dataset.primary
      );
      document.documentElement.style.setProperty('--loading-progress-bar', '#fff');
      document.documentElement.style.setProperty('--loading-dot', '#fff');
      document.documentElement.style.setProperty('--loading-text-color', '#fff');
    }
    if (document.body.dataset.secondary) {
      document.documentElement.style.setProperty('--secondary', document.body.dataset.secondary);
    }

    let config = {};

    let browserToken = sessionStorage.getItem('browser_session_token');
    sessionStorage.clear();

    if (browserToken) {
      sessionStorage.setItem('browser_session_token', browserToken);
    } else {
      sessionStorage.setItem('browser_session_token', generateId(20));
    }

    browserToken = sessionStorage.getItem('browser_session_token');

    const lockTxnRequestPromise = !isMobile
      ? Promise.resolve()
      : APIs.sendBrowserToken(browserToken)
          .then((result) => {
            if (result.status === 'error') {
              this.setState({
                error: {
                  component: LockedTransactionUrl,
                  props: {
                    transactionUrlLockExpiry: result.transactionUrlLockExpiry
                  }
                }
              });
            }
          })
          .catch(() => {});

    let checkTxnRequestPromise;

    if (window.location.pathname !== UNIVERSAL_URL_PATH || detailMatch === 'yes') {
      checkTxnRequestPromise = APIs.checkTransactionType()
        .then((result) => {
          const {
            status,
            verify,
            flowType = 'FLOW_V2',
            isCompleted = false,
            questionnaire = null,
            transStatus,
            dataProvider = null,
            hasLivenessScreen = true,
            appConfig = {},
            user_resend_count: userResendCount = 0,
            cancelRedirectUrl
          } = result;

          this.setState({ userResendCount });

          if (
            status !== 'success' ||
            transStatus === 'COMPLETED' ||
            transStatus === 'EXPIRED' ||
            transStatus === 'CANCELLED' ||
            transStatus === '404'
          ) {
            this.setState({
              flowType: 'FLOW_V2',
              transStatus: status !== 'success' ? '404' : transStatus
            });

            setCookie('detailMatch', 'false', -1);
          } else {
            this.setState({
              verify,
              flowType,
              isCompleted,
              transStatus,
              questionnaire,
              hasLivenessScreen
            });
          }

          // Set Global CSS Vars
          if (appConfig.primary) {
            document.documentElement.style.setProperty('--primary', appConfig.primary);
            document.documentElement.style.setProperty('--loading-bg', appConfig.primary);
            document.documentElement.style.setProperty(
              '--loading-progress-bar-incomplete',
              appConfig.primary
            );
            document.documentElement.style.setProperty('--loading-progress-bar', '#fff');
            document.documentElement.style.setProperty('--loading-dot', '#fff');
            document.documentElement.style.setProperty('--loading-text-color', '#fff');
            document.documentElement.style.setProperty('--loading-spinner-dot1', appConfig.primary);
            document.documentElement.style.setProperty('--loading-spinner-dot3', appConfig.primary);
          }

          if (appConfig.secondary) {
            document.documentElement.style.setProperty('--secondary', appConfig.secondary);
            document.documentElement.style.setProperty(
              '--loading-spinner-dot2',
              appConfig.secondary
            );
            document.documentElement.style.setProperty(
              '--loading-spinner-dot4',
              appConfig.secondary
            );
          }
          if (appConfig.body) {
            document.documentElement.style.setProperty('--body-text-color', appConfig.body);
            document.documentElement.style.setProperty('--heading-text-color', appConfig.body);
          }
          if (appConfig.font) {
            document.documentElement.style.setProperty('--base-font-family', appConfig.font);
          }
          if (appConfig.heading_font) {
            document.documentElement.style.setProperty(
              '--heading-font-family',
              appConfig.heading_font
            );
          }

          // Store the APP settings to store
          config = { ...config, ...appConfig, dataProvider, cancelRedirectUrl };
          setAppConfig(config);
        })
        .catch(() => {
          this.setState({
            flowType: 'FLOW_V2',
            transStatus: '404'
          });
        });
    }

    Promise.allSettled([countryRequestPromise, lockTxnRequestPromise, checkTxnRequestPromise]).then(
      () => {
        this.setState({
          isLoading: false
        });
      }
    );

    if (!isMobile) {
      if (!getCookie(MOBILE_DEVICE_CHECK_COOKIE)) {
        setCookie(MOBILE_DEVICE_CHECK_COOKIE, 'true', 1 / 24);
        if (window.location.reload) {
          window.location.reload(true);
        } else {
          // eslint-disable-next-line no-self-assign
          window.location.href = window.location.href;
        }
      } else {
        this.setState({ nonMobile: true });
      }
    }
  }

  render() {
    document.documentElement.className = isMobile ? 'mobile' : 'desktop';

    const {
      flowType,
      verify,
      isLoading,
      isCompleted,
      transStatus,
      questionnaire,
      hasLivenessScreen,
      error,
      isIncorrectUrl,
      userResendCount,
      nonMobile
    } = this.state;

    if (transStatus === 'COMPLETED') {
      return (
        <Message
          title={localizedString('app.FLOW_V2_VERIFICATION_COMPLETED_ALERT_TITLE')}
          completed
        >
          {parse(localizedString('verificationCompletedAlert'))}
        </Message>
      );
    }

    if (transStatus === 'EXPIRED') {
      const { appConfig } = this.props;
      let verificationExpiredContent;
      if (appConfig.expiryPageContent) {
        verificationExpiredContent = draftToHtml(
          getLocalizedIdvcContent(appConfig.expiryPageContent)
        );
      }

      if (!verificationExpiredContent) {
        verificationExpiredContent = localizedString('verificationExpiredAlert');
      }

      return (
        <Message title={localizedString('app.FLOW_V2_VERIFICATION_EXPIRED_ALERT_TITLE')} issue>
          {parse(verificationExpiredContent)}
        </Message>
      );
    }

    if (transStatus === 'CANCELLED') {
      return (
        <Message title={localizedString('app.FLOW_V2_SESSION_CANCELLED_ALERT_TITLE')} issue>
          {parse(localizedString('app.FLOW_V2_SESSION_CANCELLED_ALERT_DESCRIPTION'))}
        </Message>
      );
    }
    if (transStatus === '404') {
      return (
        <Message title={localizedString('app.FLOW_V2_INCORRECT_URL_ALERT_TITLE')} issue>
          {parse(localizedString('app.FLOW_V2_INCORRECT_URL_ALERT_DESCRIPTION'))}
        </Message>
      );
    }

    const { component: Error, props: errorProps } = error || {};

    if (isLoading) {
      return <LoadingSpinner heading={localizedString('pleaseWait')} />;
    }

    if (nonMobile) {
      if (isIncorrectUrl) {
        return (
          <Message title={localizedString('app.FLOW_V2_INCORRECT_URL_ALERT_TITLE')} issue>
            {parse(localizedString('app.FLOW_V2_INCORRECT_URL_ALERT_DESCRIPTION'))}
          </Message>
        );
      }

      if (NON_MOBILE_DEVICE_SCREEN_TYPES.SMS === NON_MOBILE_DEVICE_FALLBACK) {
        return <SendSMS resendCount={userResendCount} />;
      }

      if (NON_MOBILE_DEVICE_SCREEN_TYPES.QR === NON_MOBILE_DEVICE_FALLBACK) {
        return <QRVerificationPage />;
      }

      return <OnlyMobileAllowed />;
    }

    if (error) {
      return <Error {...errorProps} />;
    }

    if (flowType === 'LIVE_ONLY') {
      return (
        <AppFlowV2Container
          transStatus={transStatus}
          flowType={flowType}
          verify={verify}
          hasLivenessScreen
        />
      );
    }

    if (flowType === 'FLOW_V2' || flowType === 'DATA_ONLY') {
      return (
        <AppFlowV2Container
          transStatus={transStatus}
          flowType={flowType}
          verify={verify}
          hasLivenessScreen={hasLivenessScreen}
          questionnaire={questionnaire}
        />
      );
    }

    if (flowType === 'VOI_FLOW_V2') {
      return (
        <AppVOIFlowV2Container transStatus={transStatus} flowType={flowType} verify={verify} />
      );
    }

    if (flowType === 'Q_ONLY') {
      return (
        <AppQOnlyContainer
          isCompleted={isCompleted}
          flowType={flowType}
          verify={verify}
          questionnaire={questionnaire}
        />
      );
    }

    return (
      <AppFlowV2Container
        transStatus="404"
        flowType={flowType}
        verify={verify}
        hasLivenessScreen={hasLivenessScreen}
        questionnaire={questionnaire}
      />
    );
  }
}

AppView.propTypes = {
  appConfig: PropTypes.object,
  setAppConfig: PropTypes.func,
  setIpCountryCode: PropTypes.func
};

export default compose(
  withDeviceMotionDetectionProvider,
  withDetailMatch,
  connect(mapStateToProps, mapDispatchToProps)
)(AppView);

function mapStateToProps({ appConfig }) {
  return {
    appConfig
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setAppConfig: (data) => dispatch(AppConfigAction.setAppConfig(data)),
    setIpCountryCode: (data) => dispatch(setIpCountryCodeAction(data))
  };
}
